import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { motion } from "framer-motion";

// ==================== KEYFRAME ANIMATIONS ====================

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;


const softGlow = keyframes`
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.25; }
`;

// ==================== SUBTLE ANIMATED SVG COMPONENTS ====================

// Neural Network - Darker, more visible background animation
const NeuralNetworkSVG = () => (
  <Box
    component="svg"
    viewBox="0 0 200 150"
    sx={{
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      right: 0,
      opacity: 0.15,
    }}
  >
    {/* Darker connection lines */}
    <g stroke="#1e3a8a" strokeWidth="1.5" fill="none" opacity="0.7">
      <motion.line
        x1="30" y1="30" x2="100" y2="60"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.line
        x1="30" y1="75" x2="100" y2="60"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, delay: 0.5, repeat: Infinity }}
      />
      <motion.line
        x1="30" y1="120" x2="100" y2="90"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, delay: 1, repeat: Infinity }}
      />
      <motion.line
        x1="100" y1="60" x2="170" y2="45"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, delay: 1.5, repeat: Infinity }}
      />
      <motion.line
        x1="100" y1="90" x2="170" y2="105"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, delay: 2, repeat: Infinity }}
      />
    </g>
    {/* Darker, more visible nodes */}
    {[
      { cx: 30, cy: 30 }, { cx: 30, cy: 75 }, { cx: 30, cy: 120 },
      { cx: 100, cy: 60 }, { cx: 100, cy: 90 },
      { cx: 170, cy: 45 }, { cx: 170, cy: 105 },
    ].map((node, i) => (
      <motion.circle
        key={i}
        cx={node.cx}
        cy={node.cy}
        r="5"
        fill="#3567D6"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
      />
    ))}
  </Box>
);

// Heartbeat - Darker, more visible
const HeartbeatSVG = () => (
  <Box
    component="svg"
    viewBox="0 0 300 80"
    sx={{
      width: "60%",
      height: "50px",
      position: "absolute",
      bottom: "20px",
      right: "20px",
      opacity: 0.4,
    }}
  >
    <motion.path
      d="M0,40 L40,40 L55,40 L65,20 L75,60 L85,10 L95,70 L105,40 L120,40 L300,40"
      fill="none"
      stroke="#1e3a8a"
      strokeWidth="3"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
    />
  </Box>
);

// DNA Helix - Darker, more visible
const DNAHelixSVG = () => (
  <Box
    component="svg"
    viewBox="0 0 80 140"
    sx={{
      width: "60px",
      height: "120px",
      position: "absolute",
      right: "20px",
      top: "50%",
      transform: "translateY(-50%)",
      opacity: 0.5,
    }}
  >
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <g key={i}>
        <motion.circle
          cx="20"
          cy={15 + i * 22}
          r="6"
          fill="#1e3a8a"
          animate={{ cx: [20, 60, 20] }}
          transition={{ duration: 2.5, delay: i * 0.15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.circle
          cx="60"
          cy={15 + i * 22}
          r="6"
          fill="#2563eb"
          animate={{ cx: [60, 20, 60] }}
          transition={{ duration: 2.5, delay: i * 0.15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.line
          y1={15 + i * 22}
          y2={15 + i * 22}
          stroke="#1e3a8a"
          strokeWidth="1.5"
          strokeOpacity="0.6"
          animate={{ x1: [20, 60, 20], x2: [60, 20, 60] }}
          transition={{ duration: 2.5, delay: i * 0.15, repeat: Infinity, ease: "easeInOut" }}
        />
      </g>
    ))}
  </Box>
);

// Network nodes - Darker, more visible collaboration visualization
const CollaborationSVG = () => (
  <Box
    component="svg"
    viewBox="0 0 120 120"
    sx={{
      width: "100px",
      height: "100px",
      position: "absolute",
      right: "15px",
      top: "50%",
      transform: "translateY(-50%)",
      opacity: 0.10,
    }}
  >
    {/* Central node */}
    <motion.circle
      cx="60" cy="60" r="14"
      fill="#1e3a8a"
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    {/* Outer nodes */}
    {[0, 72, 144, 216, 288].map((angle, i) => {
      const x = 60 + 35 * Math.cos((angle * Math.PI) / 180);
      const y = 60 + 35 * Math.sin((angle * Math.PI) / 180);
      return (
        <g key={i}>
          <line x1="60" y1="60" x2={x} y2={y} stroke="#1e3a8a" strokeWidth="1.5" opacity="0.6" />
          <motion.circle
            cx={x} cy={y} r="8"
            fill="#2563eb"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
          />
        </g>
      );
    })}
  </Box>
);

// ==================== MAIN COMPONENT ====================

const KnowledgeBankSection = () => {
  // Brand-consistent blue color palette
  const brandBlue = "#3567D6";
  const brandBlueLight = "#5B8DEF";
  const brandBlueDark = "#1e40af";

  const knowledgeCards = [
    {
      title: "AI-Powered",
      subtitle: "Smart Analysis",
      description: "Advanced algorithms analyze lab reports and transform complex medical data into clear, patient-friendly insights",
      stats: [
        { value: "1000+", label: "Parameters" },
        { value: "Real-time", label: "Processing" },
      ],
      animation: NeuralNetworkSVG,
      colSpan: { xs: 12, md: 4 },
    },
    {
      title: "Physician Led",
      subtitle: "Clinical Expertise",
      description: "Board-certified physicians curate and validate all health recommendations, ensuring medically accurate and actionable guidance for patients",
      stats: [
        { value: "Expert", label: "Validated" },
        { value: "Evidence", label: "Based" },
      ],
      animation: HeartbeatSVG,
      colSpan: { xs: 12, md: 8 },
    },
    {
      title: "Ever-Evolving",
      subtitle: "Continuous Updates",
      description: "Our knowledge base expands continuously with latest medical research, peer-reviewed journals, and clinical best practices",
      stats: [
        { value: "Daily", label: "Updates" },
        { value: "Latest", label: "Research" },
      ],
      animation: DNAHelixSVG,
      colSpan: { xs: 12, md: 8 },
    },
    {
      title: "Collaborative Hub",
      subtitle: "Shared Knowledge",
      description: "Healthcare professionals contribute expertise to build a comprehensive database of validated health insights",
      stats: [
        { value: "Growing", label: "Network" },
        { value: "Trusted", label: "Sources" },
      ],
      animation: CollaborationSVG,
      colSpan: { xs: 12, md: 4 },
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        padding: { xs: "50px 16px", md: "80px 32px" },
        backgroundColor: "transparent",
        overflow: "hidden",
      }}
    >
      {/* Subtle background gradient */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          height: "80%",
          background: `radial-gradient(ellipse, rgba(53, 103, 214, 0.04) 0%, transparent 70%)`,
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      {/* Section Header - Centered, Full Width */}
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          marginBottom: { xs: "40px", md: "56px" },
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "32px", md: "48px" },
              fontWeight: 700,
              lineHeight: { xs: "42px", md: "58px" },
              color: "#0f172a",
              letterSpacing: "-0.5px",
              marginBottom: { xs: "16px", md: "20px" },
            }}
          >
            Grow faster with our{" "}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(135deg, ${brandBlue} 0%, ${brandBlueLight} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              evolving knowledge bank
            </Box>
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 400,
              lineHeight: { xs: "26px", md: "30px" },
              color: "#64748b",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            A collaborative hub where seasoned physicians contribute their clinical
            expertise through curating, editing, and peer-reviewed journal articles.
            Delivering intelligent, clinically validated, and actionable health insights.
          </Typography>
        </motion.div>
      </Box>

      {/* Feature Cards Grid */}
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gap: { xs: "16px", md: "20px" },
          position: "relative",
          zIndex: 1,
        }}
      >
        {knowledgeCards.map((card, index) => (
          <Box
            key={index}
            sx={{
              gridColumn: {
                xs: "span 12",
                md: `span ${card.colSpan.md}`,
              },
            }}
          >
            <FeatureCard card={card} index={index} brandBlue={brandBlue} brandBlueLight={brandBlueLight} brandBlueDark={brandBlueDark} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ==================== FEATURE CARD COMPONENT ====================

const FeatureCard = ({ card, index, brandBlue, brandBlueLight, brandBlueDark }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const AnimationComponent = card.animation;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -4 }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
          minHeight: { xs: "200px", md: "210px" },
          borderRadius: "20px",
          padding: { xs: "20px", md: "24px" },
          cursor: "pointer",
          overflow: "hidden",
          // Glass morphism background
          background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          // Thick dark border based on brand color
          border: `2px solid ${brandBlueDark}20`,
          // Subtle shadow
          boxShadow: isHovered
            ? `0 16px 40px rgba(53, 103, 214, 0.15)`
            : "0 4px 20px rgba(0, 0, 0, 0.04)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            border: `2px solid ${brandBlue}40`,
            background: "rgba(255, 255, 255, 0.85)",
          },
        }}
      >
        {/* Subtle background glow - far from content */}
        <Box
          sx={{
            position: "absolute",
            top: "-30%",
            right: "-30%",
            width: "60%",
            height: "60%",
            background: `radial-gradient(circle, ${brandBlue}10 0%, transparent 70%)`,
            opacity: isHovered ? 0.6 : 0.3,
            transition: "opacity 0.4s ease",
            pointerEvents: "none",
            animation: `${softGlow} 4s ease-in-out infinite`,
            zIndex: 1,
          }}
        />
        
        {/* Text readability overlay - subtle white gradient behind text */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(to right, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              marginBottom: "8px",
              position: "relative",
              zIndex: 3,
            }}
          >
            {/* Accent badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 8px",
                borderRadius: "12px",
                background: `${brandBlue}10`,
                marginBottom: "6px",
              }}
            >
              <Box
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: brandBlue,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: brandBlue,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {card.subtitle}
              </Typography>
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "20px", md: "22px" },
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                marginBottom: "4px",
              }}
            >
              {card.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "13px", md: "14px" },
                fontWeight: 500,
                color: "#475569",
                lineHeight: 1.6,
                maxWidth: card.colSpan.md === 8 ? "500px" : "100%",
                position: "relative",
                zIndex: 3,
                textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)",
              }}
            >
              {card.description}
            </Typography>
          </Box>

          {/* Stats */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: "16px", md: "24px" },
              marginTop: "auto",
              paddingTop: "8px",
            }}
          >
            {card.stats.map((stat, i) => (
              <Box key={i}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: { xs: "18px", md: "20px" },
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${brandBlue} 0%, ${brandBlueLight} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#94a3b8",
                    marginTop: "3px",
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Animated SVG - Background only, very subtle */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: card.colSpan.md === 8 ? "40%" : "100%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <AnimationComponent />
        </Box>
      </Box>
    </motion.div>
  );
};

export default KnowledgeBankSection;
